import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SignOutButton, useUser } from '@/shared/auth';
import { useBrand } from '@/shared/brand/useBrand';
import { PageHeader } from '@/shared/components/PageHeader';
import { Card } from '@/shared/components/ui/Card';
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher';
import { useI18n } from '@/shared/i18n/useT';
import { ThemeSwitcher } from '@/shared/theme/ThemeSwitcher';

/**
 * Settings for the end client (procalendar.app).
 * Single column, stacked — this screen is used almost exclusively on a phone.
 */
export function ClientSettings() {
  const { t } = useI18n();
  const brand = useBrand();
  const user = useUser();

  useEffect(() => {
    document.title = `${t('settings.title')} · ${brand.name}`;
  }, [t, brand.name]);

  return (
    <>
      <PageHeader title={t('settings.title')} description={t('settings.subtitle')} />

      <div className="flex flex-col gap-4">
        <Card
          title={t('settings.language.title')}
          description={t('settings.language.description')}
        >
          <LocaleSwitcher />
        </Card>

        <Card
          title={t('settings.theme.title')}
          description={t('settings.theme.description')}
        >
          <ThemeSwitcher />
        </Card>

        {/* Unlike numtable's, this screen is reachable signed out: a client can
            change language and theme before ever having an account. */}
        <Card title={t('auth.session.title')}>
          <p className="mb-3 break-words text-sm text-muted">
            {user
              ? t('auth.session.signedInAs', { email: user.email ?? '' })
              : t('auth.session.signedOut')}
          </p>
          {user ? (
            <SignOutButton />
          ) : (
            <Link
              to="/signin"
              className="tap-target inline-flex items-center justify-center rounded-token bg-accent px-4 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
            >
              {t('auth.signIn.title')}
            </Link>
          )}
        </Card>
      </div>

      <p className="mt-4 text-xs text-muted">{t('settings.storedLocally')}</p>
    </>
  );
}
